/**
 * Market mismatch prompt (AAS-168)
 *
 * Opens <info-drawer id="MarketMismatchDrawer"> offering to switch the visitor
 * to the market their IP says they're in.
 *
 * This file is the UI half only. The data, the routing rules and the pre-paint
 * homepage auto-redirect live in snippets/market-early-redirect.liquid, which
 * publishes `window.marketContext` (.markets, .resolve(), .applySuggestion()).
 * Detection is owned by snippets/market-geo-prefetch.liquid via
 * `window.marketGeo` — a synchronous localStorage hit, or an in-flight fetch
 * started back in <head>. Nothing here issues its own request.
 *
 * Two paths reach this file:
 *   - Cache hit, but the early snippet declined to redirect (not the homepage,
 *     or the International fallback, or already dismissed) -> popup.
 *   - Cache miss (first visit): the early snippet had nothing synchronous to
 *     act on, so the auto-redirect decision is repeated here once the fetch
 *     lands, and otherwise falls through to the popup.
 */
(() => {
  // Also written by assets/header-localization.js — switching market by hand in
  // the header/footer/nav switcher pre-dismisses this prompt so it can't fire on
  // the page the switch lands on. Keep the key in sync with that file.
  const DISMISS_KEY = 'marketMismatchDismissed';
  // One-shot guard so a homepage auto-redirect can't loop if the market never
  // actually changes (misconfig / geo flap): after redirecting once, later loads
  // fall through to the popup instead. Also set by market-early-redirect.liquid.
  const REDIRECT_KEY = 'marketAutoRedirected';
  const COUNTRY_TOKEN = '__country__';

  // Don't nag inside the theme editor.
  if (window.Shopify && window.Shopify.designMode) return;

  const init = () => {
    const drawer = document.getElementById('MarketMismatchDrawer');
    const ctx = window.marketContext;
    if (!drawer || !ctx || !ctx.current) return;

    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch (e) {
      // sessionStorage unavailable (private mode) — carry on without persistence.
    }

    detectCountry().then((detected) => {
      if (!detected || detected === ctx.current) return;

      const suggestion = ctx.resolve(detected);
      if (!suggestion) return;
      suggestion.name = countryName(detected);

      // Cache-miss path: the early snippet couldn't act synchronously, so the
      // auto-redirect decision happens here instead. Same rules, and ctx.isHome
      // already carries them — primary-market homepage only (a prefixed URL
      // such as /fr-ca has stated its market), concrete market of their own
      // only, never the International fallback, once per session.
      if (ctx.isHome && !suggestion.international && !hasRedirected()) {
        markRedirected();
        if (ctx.applySuggestion(suggestion)) return;
      }

      renderBody(drawer, suggestion);

      const switchBtn = drawer.querySelector('[data-mismatch-switch]');
      if (switchBtn) {
        switchBtn.addEventListener('click', (e) => {
          e.preventDefault();
          dismiss();
          ctx.applySuggestion(suggestion);
        });
      }
      drawer.querySelectorAll('[data-mismatch-stay]').forEach((el) => {
        el.addEventListener('click', dismiss);
      });

      const chooseBtn = drawer.querySelector('[data-mismatch-choose]');
      if (chooseBtn) {
        chooseBtn.addEventListener('click', () => {
          dismiss();
          // The button also carries data-drawer-close, so the info-drawer
          // shell is closing right now. Hand over to the real country
          // selector only after its 0.3s close transition has finished —
          // opening the nav drawer earlier races the body scroll-unlock.
          setTimeout(openCountrySelector, 350);
        });
      }

      drawer.open();
    });
  };

  /**
   * The visitor's country from the shared geo owner: a synchronous cache hit,
   * else the fetch started in <head>. Resolves to null when detection failed or
   * the prefetch snippet never ran.
   */
  const detectCountry = async () => {
    const geo = window.marketGeo;
    if (!geo) return null;
    if (geo.cached) return geo.cached;
    if (!geo.promise) return null;
    try {
      return await geo.promise;
    } catch (e) {
      return null;
    }
  };

  /**
   * Fill the heading ("Hi! It looks like you're in France.") and the accept
   * button, which reads as the destination: flag + country name, or globe +
   * "International" when the detected country only resolves to the fallback.
   *
   * Copy comes from data attributes rather than Liquid output because the
   * country is client-detected. `dataset` decodes the entities `| t` emitted,
   * so assigning to textContent is safe.
   */
  // The token can sit in EITHER slot depending on the locale: de/cs/es/fr/it/pl
  // carry it in `localization.mismatch.title`, en carries it in
  // `localization.mismatch.body`. Only the title was ever interpolated, so
  // English rendered the literal "__country__" while every other language read
  // fine. Substituting in both slots covers either arrangement and needs no
  // copy changes.
  //
  // The body is server-rendered by Liquid (so it still reads correctly with JS
  // off) and is rewritten in place here. Its original text is kept in this map
  // rather than read back off the element, because after the first open the
  // token is gone from the DOM and a second open — different detected country,
  // same drawer — would otherwise have nothing left to replace.
  const bodyTemplates = new WeakMap();

  // replace() only swaps the first occurrence; split/join covers a locale that
  // names the country twice.
  const withCountry = (template, name) => template.split(COUNTRY_TOKEN).join(name);

  const renderBody = (drawer, suggestion) => {
    const title = drawer.querySelector('[data-mismatch-title]');
    if (title) {
      title.textContent = withCountry(drawer.dataset.title || '', suggestion.name);
    }

    // Matched on the class because that is the hook the Liquid already gives
    // this paragraph — adding a data attribute would mean touching the snippet
    // for nothing.
    const body = drawer.querySelector('.market-mismatch__text');
    if (body) {
      if (!bodyTemplates.has(body)) bodyTemplates.set(body, body.textContent);
      body.textContent = withCountry(bodyTemplates.get(body), suggestion.name);
    }

    const label = drawer.querySelector('[data-mismatch-country]');
    if (label) {
      label.textContent = suggestion.international
        ? drawer.dataset.internationalLabel || suggestion.name
        : suggestion.name;
    }

    const flagImg = drawer.querySelector('[data-mismatch-flag]');
    const globe = drawer.querySelector('[data-mismatch-globe]');
    if (suggestion.international || !suggestion.flag) {
      if (flagImg) flagImg.hidden = true;
      if (globe) globe.hidden = false;
    } else {
      if (globe) globe.hidden = true;
      if (flagImg) {
        flagImg.src = suggestion.flag;
        flagImg.alt = '';
        flagImg.hidden = false;
      }
    }
  };

  // Country name in the active storefront locale — this string is read by the
  // visitor, so it follows <html lang> rather than being pinned to English.
  const countryName = (iso) => {
    if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return iso;
    if (!iso || iso.length !== 2) return iso;
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.DisplayNames([locale, 'en'], { type: 'region' }).of(iso) || iso;
    } catch (e) {
      try {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) || iso;
      } catch (e2) {
        return iso;
      }
    }
  };

  /**
   * AAS-5265: "Choose country" opens the selector the theme already has
   * instead of an inline panel inside the prompt. Desktop: the header globe
   * toggle — header-localization.js opens its panel and focuses the search
   * input itself. Mobile (globe hidden): the nav drawer's localization panel,
   * opened via the NavDrawer API; the search field gets focus once the
   * panel-switch animation settles (no keyboard auto-raise — focus() outside
   * the tap gesture doesn't open one, which is fine here).
   */
  const openCountrySelector = () => {
    const globe = document.querySelector('.header__icon--globe.header-localization__toggle');
    if (globe && globe.offsetParent !== null) {
      globe.click();
      return;
    }
    const nav = document.querySelector('nav-drawer');
    if (!nav || typeof nav.open !== 'function') return;
    nav.open(document.querySelector('.header__burger'));
    nav.navigateTo('localization');
    const search = nav.querySelector('.nav-drawer__panel--localization input[type="search"]');
    if (search) setTimeout(() => search.focus(), 400);
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      // Ignore — drawer just reopens next load if storage is blocked.
    }
  };

  const hasRedirected = () => {
    try {
      return sessionStorage.getItem(REDIRECT_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const markRedirected = () => {
    try {
      sessionStorage.setItem(REDIRECT_KEY, '1');
    } catch (e) {
      // Ignore — worst case the redirect is retried next load.
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
